import type { FeatureKey } from "./features";
import type { NavGroup } from "./navigation";
import { TONES } from "@people-ai/planets";

/** Each agent is a workspace that owns the frame while you are in it. Front desk, sales, workforce
 * and knowledge screens live only in Sarah, Shelby, Olivia and Michael, purchasing and menus in
 * Nancy; You is a view Core also covers. Settings' own addresses always open in Settings. */
export type WorkspaceId =
  | "aria"
  | "sarah"
  | "shelby"
  | "amy"
  | "olivia"
  | "michael"
  | "grace"
  | "nancy"
  | "david"
  | "core"
  | "comms"
  | "you"
  | "settings"
  | "tenant"
  | "support-desk"
  | "luna"
  | "pam"
  | "abby";

export interface Workspace {
  id: WorkspaceId;
  label: string;
  /** The sentence under the label in the switcher. What it is FOR, not what it contains. */
  blurb: string;
  /** Where switching to it lands when there is no better answer. */
  home: string;
  /** The nav groups whose screens are this workspace's rail. Empty where content is not a list of
   * screens (Aria is a conversation). */
  groups: readonly NavGroup[];
  /** Single screens claimed out of another workspace's groups (the You workspace cuts across
   * Communications, Support and Administration). A claim wins over the group so each screen has one
   * home, and is listed whether its surface is the rail or the settings hub. */
  items?: readonly FeatureKey[];
  /** Route prefixes meaning "you are here", matched longest-first (`/settings/lead-fields` is
   * Settings though CRM owns a screen there). Derived from the URL so a pasted link opens in the
   * right workspace and a reload keeps it. */
  owns: readonly string[];
  /** Its colour, for the rail's header, active row and focus rings, never page content. An agent's
   * is its planet's ink (packages/planets/src/tones.json); a place keeps its own. */
  accent: string;
}

/** Order of the switcher and the 1-6 keys: You, Core, then Aria, Sarah, Nancy, then Settings, last
 * everywhere. The platform console's list is `PLATFORM_WORKSPACES` in
 * `workspace-list-platform.ts`. */
export const WORKSPACES: readonly Workspace[] = [
  {
    id: "you",
    label: "You",
    blurb: "What is waiting on you, and what is yours.",
    home: "/you",
    groups: [],
    items: ["mentions", "task-center", "settings", "menu"],
    /* Three of these are not screens the registry lists — your day, the
       signature queue and your own HR file — so they are claimed by address. */
    owns: [
      "/you",
      "/mentions",
      "/task-center",
      "/signature-queue",
      "/hr/my-documents",
      "/settings/profile",
      "/settings/menu",
    ],
    accent: "#C0467D",
  },
  {
    id: "core",
    label: "Core",
    blurb: "The building: residents, medications, money, people.",
    home: "/",
    groups: ["main", "care", "pharmacy", "communications", "support", "financials"],
    // Nothing: Core is where a path lands when no other workspace claims it.
    owns: [],
    accent: "#A1683C",
  },
  /* A place, not an agent: the team's own messages, threads and meetings, free on every plan.
     The phone's Comms, in the same colour. */
  {
    id: "comms",
    label: "Comms",
    blurb: "Messages, threads and meetings with your team.",
    home: "/messaging",
    groups: [],
    items: ["messaging", "meetings"],
    owns: ["/messaging"],
    accent: "#6A3FC4",
  },
  {
    id: "aria",
    label: "Aria",
    blurb: "Ask in plain English. She proposes, you confirm.",
    home: "/aria",
    groups: [],
    owns: ["/aria"],
    accent: TONES.aria.ink,
  },
  {
    id: "sarah",
    label: "Sarah",
    blurb: "The front desk: calls, texts and who is on the line.",
    home: "/ws/sarah/phone",
    groups: ["front-desk"],
    owns: ["/ws/sarah", "/sarah"],
    accent: TONES.sarah.ink,
  },
  {
    id: "shelby",
    label: "Shelby",
    blurb: "Sales: leads, tours, referral sources and campaigns.",
    home: "/crm",
    groups: ["crm", "sales-assistant"],
    owns: ["/crm", "/call-logs", "/shelby"],
    accent: TONES.shelby.ink,
  },
  {
    id: "amy",
    label: "Amy",
    blurb: "Move-ins: from the lead to the chart, until every step is done.",
    home: "/ws/amy",
    groups: ["move-ins"],
    owns: ["/ws/amy"],
    accent: TONES.amy.ink,
  },
  {
    id: "olivia",
    label: "Olivia",
    blurb:
      "Workforce: hiring, training, HR files and the schedule. Ask, she prepares, you confirm.",
    /* Her conversation; somebody who cannot talk to her lands on the first screen they can open
     * (`landingFor`). */
    home: "/ws/olivia",
    groups: ["workforce"],
    /* A person's record and the register are user management, open on every plan; their HR
     * parts check HR's own feature. */
    owns: ["/ws/olivia", "/hiring", "/training", "/hr", "/schedule"],
    accent: TONES.olivia.ink,
  },
  {
    id: "michael",
    label: "Michael",
    blurb: "Knowledge: the policies and the knowledge base.",
    home: "/knowledgebase",
    groups: ["knowledge"],
    owns: ["/policies", "/knowledgebase"],
    accent: TONES.michael.ink,
  },
  {
    id: "grace",
    label: "Grace",
    blurb: "The front door: visitors, badges and who is in the building.",
    home: "/visitors/grace",
    groups: ["visitors"],
    /* Her conversations, the insights and the kiosk; the visitor log itself is Core's. */
    owns: ["/visitors/grace", "/visitors/insights", "/kiosk"],
    accent: TONES.grace.ink,
  },
  {
    id: "nancy",
    label: "Nancy",
    blurb: "Ordering: shopping lists and purchase orders.",
    home: "/ws/nancy",
    groups: ["purchasing"],
    /* Menus and Supplies too: what the kitchen cooks and the closet holds is what she buys. */
    owns: ["/ws/nancy", "/menus", "/supplies", "/agents/menus"],
    accent: TONES.nancy.ink,
  },
  {
    id: "david",
    label: "David",
    blurb: "Maintenance: work orders, the parts shop and the parts to buy.",
    home: "/ws/david",
    groups: ["maintenance"],
    owns: ["/ws/david", "/maintenance"],
    accent: TONES.david.ink,
  },
  {
    id: "settings",
    label: "Settings",
    blurb: "How this organization is set up, and how each workspace behaves.",
    /* Not "/settings": that address redirects to your own profile, which now
       lives in You. Somebody who cannot reach this screen is sent to the first
       Settings screen they can — see `landingFor`. */
    home: "/ws/settings",
    groups: ["administration"],
    /* The number bank too: Core's lines, at Sarah's old address so bookmarks still land. */
    owns: ["/ws/settings", "/settings", "/sarah/numbers"],
    accent: "#4B4B5C",
  },
];
